import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormCard } from "Components/Form/FormCard";
import { I18N_NS } from "Config/i18n";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { Button, Input, Popconfirm, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { notificationSuccess } from "utils/libHelpers/AntdNotification";
import { formatMcpTs } from "./mcpAdminFormat";
import { McpAdminFeedback } from "./McpAdminFeedback";

export const McpRegistrationsTab = () => {
  const { t } = useTranslation(I18N_NS.ADMIN);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<API_McpOauthRegistration[]>([]);
  const [query, setQuery] = useState("");
  const [feedback, setFeedback] = useState<string | null>(null);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const data = await API.getData<API_McpOauthRegistration[]>(API_URL.MCP_ADMIN.getRegistrationsLast24h());
      setRows(data ?? []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return rows;
    }
    return rows.filter((row) => {
      const hay = [row.ipAddress, row.clientId, row.clientName, row.userId, row.userName, row.userLogin]
        .map((value) => String(value ?? "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [rows, query]);

  const deleteForIp = async (ip: string) => {
    await API.deleteData(API_URL.MCP_ADMIN.deleteRegistrationsLast24h(), { ip });
    await fetchRows();
    const msg = t("DCR registrations deleted");
    notificationSuccess(t("Success"), msg);
    setFeedback(msg);
  };

  const columns: ColumnsType<API_McpOauthRegistration> = [
    { dataIndex: "ipAddress", title: t("IP address") },
    {
      dataIndex: "userId",
      title: t("User ID"),
      width: 110,
      render: (value: number | undefined) => (value != null ? value : "—"),
    },
    {
      dataIndex: "userName",
      title: t("User"),
      ellipsis: true,
      render: (value: string | undefined) => value || "—",
    },
    {
      title: t("OAuth client_id"),
      ellipsis: true,
      render: (_v, record) =>
        record.clientId || (record.clientApplicationId != null ? `#${record.clientApplicationId}` : "—"),
    },
    {
      dataIndex: "clientName",
      title: t("Client name"),
      ellipsis: true,
      render: (value: string) => value || "—",
    },
    {
      dataIndex: "created",
      title: t("Created at"),
      width: 220,
      render: (value: string | number) => formatMcpTs(value),
    },
    {
      title: t("Actions"),
      width: 80,
      render: (_v, record) =>
        record.ipAddress ? (
          <Popconfirm title={t("Delete last 24 hours for this IP?")} onConfirm={() => deleteForIp(record.ipAddress)}>
            <Button type="link" size="small" aria-label={t("Delete DCR registrations for this IP")}>
              <FontAwesomeIcon icon={["far", "trash-alt"]} />
            </Button>
          </Popconfirm>
        ) : null,
    },
  ];

  return (
    <FormCard title={t("Registrations")} loading={loading}>
      <McpAdminFeedback message={feedback} />
      <p className="mb-2">
        {t(
          "OAuth Dynamic Client Registrations from the last 24 hours. " +
            "This is the DCR cap (10 per IP), not the admission allowlist (Clients tab). " +
            "OAuth client_id is the UUID of the app registered via DCR — " +
            "the same id as on the Clients tab, not the internal row number. " +
            "User ID is the PAVE account that later completed authorize for this app " +
            "(empty until someone has signed in)."
        )}
      </p>
      <Input
        allowClear
        className="mb-2"
        aria-label={t("Search registrations")}
        placeholder={t("IP, user, or OAuth client_id")}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <Table
        rowKey={(row) => String(row.id ?? `${row.ipAddress}-${row.created}`)}
        columns={columns}
        dataSource={filtered}
        size="small"
        pagination={false}
      />
    </FormCard>
  );
};
